import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import swal from "sweetalert2";

import { PromptsService } from "../prompts.service";
import {
  Prompt,
  PromptArea,
  PromptSeccion,
  SistemaIa,
} from "../../shared/model/prompts/prompt.model";

/**
 * Un área dentro del panel "Quién lo ve": lo que viene del servidor más
 * lo que el administrador marcó y todavía no guarda. Se separan para que
 * Cancelar deje todo como estaba y para poder decir qué va a cambiar.
 */
interface AreaMarcable extends PromptArea {
  marcada: boolean;
}

/**
 * El catálogo de prompts.
 *
 * Una sola pantalla para todos. Quien tiene el perfil de administración
 * ve además los botones de alta, edición y baja; el resto sólo consulta
 * y copia, que es para lo que existe el módulo.
 */
@Component({
  selector: "app-catalogo-prompts",
  standalone: true,
  // NgClass va explícito: en standalone, una directiva que no se importa
  // no da error de compilación, simplemente no hace nada.
  imports: [CommonModule, FormsModule, MatTooltipModule],
  templateUrl: "./catalogo-prompts.component.html",
  styleUrls: ["./catalogo-prompts.component.css"],
})
export class CatalogoPromptsComponent implements OnInit, OnDestroy {
  secciones: PromptSeccion[] = [];
  sistemas: SistemaIa[] = [];
  puedeAdministrar = false;

  /** Cuánta gente ve hoy el módulo. Sólo llega a quien administra. */
  lectores: number | null = null;

  /** Las áreas tal como las manda el servidor. Sólo a quien administra. */
  private areasServidor: PromptArea[] = [];

  cargando = true;
  error = "";

  /* ---------- Filtros ---------- */

  private _texto = "";

  /**
   * El área abierta. Siempre hay una.
   *
   * No existe un "Todas": con 25 prompts repartidos en 10 áreas, verlas
   * juntas obliga a desplazarse, que es justo lo que las pestañas
   * evitan. Se ve un área a la vez y cabe entera.
   */
  tabActiva: number | null = null;

  abrirTab(s: PromptSeccion): void {
    this.tabActiva = s.idPromptSeccion ?? null;
    this.pagina = 1;
  }

  get texto(): string {
    return this._texto;
  }

  set texto(v: string) {
    this._texto = v;
    this.pagina = 1;
  }

  /**
   * Lo que se busca, ya listo para comparar: en minúsculas y SIN acentos.
   *
   * Diez de los quince títulos llevan acento. Sin esto, "porque" no
   * encontraba "el porqué" y "revision" no encontraba "Revisión": quien
   * escribe rápido no pone tildes, y el catálogo sí.
   */
  private get busca(): string {
    return this.normaliza(this.texto.trim());
  }

  private normaliza(s: string): string {
    // NFD separa la letra de su tilde; el rango son las marcas
    // diacríticas combinantes, que es lo que se quita.
    return (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "");
  }

  /* ---------- Filtros ----------
   *
   * Uno solo: el orden. Hubo un segundo, por asistente (Codex / Claude),
   * y se quitó el 10/09/2026: los prompts sirven con cualquiera de los
   * dos, así que filtrar por eso era escoger entre dos montones que en
   * realidad son el mismo. Lo que sí distingue un prompt de otro es el
   * área, y eso ya lo dicen las pestañas. */

  orden: "" | "az" | "corto" = "";

  readonly ORDENES: { clave: "" | "az" | "corto"; texto: string }[] = [
    { clave: "", texto: "Catálogo" },
    { clave: "az", texto: "A–Z" },
    { clave: "corto", texto: "Más corto" },
  ];

  /** Los del área que pasan la búsqueda. */
  get visiblesSinIa(): Prompt[] {
    const s = this.seccionActiva;
    if (!s) {
      return [];
    }
    const busca = this.busca;
    return (s.prompts || []).filter((p) => this.coincideTexto(p, busca));
  }


  ordenar(o: "" | "az" | "corto"): void {
    this.orden = o;
    this.pagina = 1;
  }

  /* ---------- Paginado ----------
   *
   * 12, 24 y 48: se dividen entre 2, 3 y 4, las columnas que caben según
   * el ancho, así que la última fila de una página siempre va completa.
   * Con 10, 20 y 50 quedaba una fila coja. */

  pagina = 1;
  porPagina = 12;
  readonly TAMANOS = [12, 24, 48];

  /** La cabecera del área abierta: a donde se sube al cambiar de página. */
  @ViewChild("cabArea") cabArea?: ElementRef<HTMLElement>;

  /* ---------- Estado de la pantalla ---------- */

  /** El prompt abierto en el modal de detalle. */
  detalle: Prompt | null = null;

  /** El que se acaba de copiar, para el aviso momentáneo. */
  copiado: number | null = null;

  /** El temporizador del aviso, para cancelarlo si se copia otro o se sale. */
  private timerCopiado?: ReturnType<typeof setTimeout>;

  /* ---------- Edición ---------- */

  editando: Prompt | null = null;
  editandoSeccion: PromptSeccion | null = null;
  guardando = false;

  /* ---------- Quién lo ve ---------- */

  /** Las áreas del panel abierto, o null si está cerrado. */
  areas: AreaMarcable[] | null = null;
  guardandoAreas = false;

  /* ---------- Retirados ---------- */

  /** Cuántos hay dados de baja. Llega en cada carga, para el botón. */
  cuantosRetirados = 0;

  /** La lista de la papelera abierta, o null si está cerrada. */
  retirados: Prompt[] | null = null;
  cargandoRetirados = false;

  /** El que se está recuperando, para desactivar sólo su botón. */
  restaurando: number | null = null;

  constructor(
    private service: PromptsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  ngOnDestroy(): void {
    clearTimeout(this.timerCopiado);
  }

  /**
   * Escape cierra lo que esté abierto, empezando por lo que está encima.
   * Es lo que hace la X de cada modal, así que no se pierde nada que la
   * X no perdiera también.
   */
  @HostListener("document:keydown.escape")
  cerrarConEscape(): void {
    // Con un aviso de sweetalert abierto, el Escape es para él.
    // SweetAlert2 escucha el teclado en su propio aviso y corta la
    // propagación, así que con el foco adentro el Escape no llega aquí;
    // esto cubre el aviso abierto con el foco fuera de él, que si no
    // cerraba también el formulario de atrás, con todo lo escrito.
    if (swal.isVisible()) {
      return;
    }
    if (this.retirados) {
      this.cerrarRetirados();
    } else if (this.areas) {
      this.cerrarAreas();
    } else if (this.editando || this.editandoSeccion) {
      // Guardando, el resultado está por llegar: si falla, el formulario
      // tiene que seguir ahí con lo escrito.
      if (this.guardando) {
        return;
      }
      // Un Escape de más no debe tirar un prompt de miles de caracteres.
      // Sin cambios se cierra sin preguntar, como siempre.
      if (this.hayCambiosSinGuardar) {
        this.confirmaDescartar();
        return;
      }
      this.cancelar();
    } else if (this.detalle) {
      this.cerrarDetalle();
    } else {
      return;
    }
    this.cdr.detectChanges();
  }

  cargar(): void {
    this.cargando = true;
    this.service.consultar().subscribe({
      next: (res) => {
        this.cargando = false;
        if (res.codigoError) {
          this.error = res.mensajeError || "No se pudo cargar el catálogo.";
          this.cdr.detectChanges();
          return;
        }
        this.error = "";
        this.secciones = res.secciones || [];
        this.sistemas = res.sistemas || [];
        this.puedeAdministrar = !!res.puedeAdministrar;
        this.areasServidor = res.areas || [];
        this.lectores = res.lectores ?? null;
        this.cuantosRetirados = res.cuantosRetirados ?? 0;

        // Se conserva la pestaña abierta si sigue existiendo: al guardar
        // un prompt se recarga todo, y volver siempre a la primera
        // sacaría al usuario del área donde estaba trabajando.
        const sigue = this.secciones.some(
          (s) => s.idPromptSeccion === this.tabActiva
        );
        if (!sigue) {
          this.tabActiva = this.secciones[0]?.idPromptSeccion ?? null;
        }
        // Si la recarga dejó menos páginas que la que se estaba viendo
        // —se eliminó el último de la última—, se recorre a la que hay
        // en vez de quedar en blanco.
        this.pagina = Math.min(this.pagina, this.totalPaginas);
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.error = "No se pudo cargar el catálogo.";
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * El "Cargando…" a pantalla completa sólo la primera vez. En las
   * recargas que siguen a un guardado, el catálogo se queda en su sitio
   * mientras llega el nuevo: verlo desaparecer y volver en cada alta lo
   * hacía sentir roto.
   */
  get cargandoPrimeraVez(): boolean {
    return this.cargando && !this.secciones.length;
  }

  /* ================= Lo que se ve ================= */

  /** El área abierta, tal como viene del servidor. */
  get seccionActiva(): PromptSeccion | null {
    return (
      this.secciones.find((s) => s.idPromptSeccion === this.tabActiva) || null
    );
  }

  /**
   * Cuántos prompts del área coinciden con la búsqueda.
   *
   * Va en la pestaña. Buscando, el contador de cada una convierte la
   * barra de pestañas en el mapa del resultado: se ve de un golpe en
   * qué áreas hay algo, sin entrar a probarlas una por una.
   */
  coincidencias(s: PromptSeccion): number {
    const busca = this.busca;
    return (s.prompts || []).filter((p) => this.coincide(p, busca)).length;
  }

  /** Los prompts del área abierta que pasan búsqueda, filtro y orden. */
  get visibles(): Prompt[] {
    const s = this.seccionActiva;
    if (!s) {
      return [];
    }
    const busca = this.busca;
    const lista = (s.prompts || []).filter((p) => this.coincide(p, busca));
    if (!this.orden) {
      return lista;
    }
    // slice() antes de ordenar: sort muta, y esta lista es la del
    // servidor. Ordenarla en su sitio cambiaría el catálogo de verdad.
    return lista.slice().sort((a, b) =>
      this.orden === "az"
        ? (a.titulo || "").localeCompare(b.titulo || "", "es")
        : this.lineas(a) - this.lineas(b)
    );
  }

  private coincide(p: Prompt, busca: string): boolean {
    return this.coincideTexto(p, busca);
  }

  private coincideTexto(p: Prompt, busca: string): boolean {
    if (!busca) {
      return true;
    }
    return (
      this.normaliza(p.titulo || "").includes(busca) ||
      this.normaliza(p.paraQueSirve || "").includes(busca) ||
      this.normaliza(p.textoPrompt || "").includes(busca) ||
      this.normaliza(p.nombreIa || "").includes(busca)
    );
  }

  get totalVisible(): number {
    return this.visibles.length;
  }

  get total(): number {
    return this.secciones.reduce((n, s) => n + (s.prompts || []).length, 0);
  }

  /** Sólo aparece cuando hay de más: con 8 prompts estorba. */
  get hayPaginado(): boolean {
    return this.totalVisible > this.porPagina;
  }

  /**
   * La barra del paginado. Se ve en cuanto hay más de lo que cabe en el
   * tamaño más chico, aunque con el tamaño escogido quepa todo: antes se
   * iba con `hayPaginado`, y quien escogía 50 se quedaba sin la barra
   * para volver a 10.
   */
  get hayPie(): boolean {
    return this.totalVisible > this.TAMANOS[0];
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.totalVisible / this.porPagina));
  }

  /** Los prompts de la página actual dentro del área abierta. */
  get enPagina(): Prompt[] {
    const todos = this.visibles;
    if (!this.hayPaginado) {
      return todos;
    }
    const desde = (this.pagina - 1) * this.porPagina;
    return todos.slice(desde, desde + this.porPagina);
  }

  /**
   * Los números que se pintan.
   *
   * Con muchas páginas no se listan todas: se muestra una ventana
   * alrededor de la actual, con los extremos siempre presentes y "…"
   * en los huecos. Sin esto, 40 páginas dan 40 botones.
   */
  get numerosPagina(): (number | "…")[] {
    const total = this.totalPaginas;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const act = this.pagina;
    const nums = new Set<number>([1, total, act]);
    if (act - 1 > 1) {
      nums.add(act - 1);
    }
    if (act + 1 < total) {
      nums.add(act + 1);
    }
    // Con la actual pegada a un extremo, la ventana se corre hacia el
    // otro lado: si no, quedan tres botones y dos puntos suspensivos.
    if (act <= 3) {
      nums.add(2).add(3).add(4);
    }
    if (act >= total - 2) {
      nums.add(total - 1).add(total - 2).add(total - 3);
    }

    const ordenados = [...nums].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
    const salida: (number | "…")[] = [];
    let previo = 0;
    for (const n of ordenados) {
      if (previo && n - previo > 1) {
        salida.push("…");
      }
      salida.push(n);
      previo = n;
    }
    return salida;
  }

  irA(p: number | "…"): void {
    if (p === "…") {
      return;
    }
    const antes = this.pagina;
    this.pagina = Math.min(Math.max(1, p), this.totalPaginas);
    if (this.pagina !== antes) {
      this.subeALaLista();
    }
  }

  cambiaTamano(n: number): void {
    if (n === this.porPagina) {
      return;
    }
    this.porPagina = n;
    this.pagina = 1;
    this.subeALaLista();
  }

  /**
   * Al cambiar de página, la lista se ve desde el principio. La lista ya
   * no rueda en su caja sino con la página, y quien pulsa «2» en la barra
   * de abajo quedaba viendo el final de la página nueva. Sólo sube si la
   * cabecera del área quedó arriba, fuera de la vista.
   */
  private subeALaLista(): void {
    setTimeout(() => {
      const cab = this.cabArea?.nativeElement;
      if (!cab) {
        return;
      }
      // Quién rueda depende del shell: con PerfectScrollbar (escritorio de
      // 960px o más, no Mac) rueda .main-panel; sin él (Mac, iPad,
      // celular) rueda la ventana, .main-panel se mueve con su contenido y
      // la barra fija de arriba tapa sus primeros 68px.
      const marco = cab.closest(".main-panel") as HTMLElement | null;
      const ruedaMarco =
        !!marco && document.documentElement.classList.contains("perfect-scrollbar-on");
      const tope = ruedaMarco
        ? marco!.getBoundingClientRect().top
        : document.querySelector(".navbar-solser-shell")?.getBoundingClientRect().bottom ?? 0;
      const dy = cab.getBoundingClientRect().top - tope;
      if (dy >= 0) {
        return;
      }
      const quieto =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      (ruedaMarco ? marco! : window).scrollBy({
        top: dy - 16,
        behavior: quieto ? "auto" : "smooth",
      });
    });
  }

  /** El rango que se está viendo, para el texto del pie. */
  get desdeVisible(): number {
    return this.totalVisible === 0 ? 0 : (this.pagina - 1) * this.porPagina + 1;
  }

  get hastaVisible(): number {
    return Math.min(this.pagina * this.porPagina, this.totalVisible);
  }

  /** Lo que ACOTA la lista: búsqueda y asistente. El orden no quita nada. */
  get hayFiltros(): boolean {
    return this.texto.trim() !== "";
  }

  /** Lo que se puede limpiar: filtros y también el orden. */
  get hayAjustes(): boolean {
    return this.hayFiltros || this.orden !== "";
  }

  limpiaFiltros(): void {
    this.orden = "";
    this.texto = "";
  }

  /** Cuántas coincidencias hay FUERA del área abierta. */
  get coincidenciasFuera(): number {
    return this.secciones
      .filter((s) => s.idPromptSeccion !== this.tabActiva)
      .reduce((n, s) => n + this.coincidencias(s), 0);
  }

  /** La primera área que sí tiene resultados, para poder saltar a ella. */
  get primeraConResultados(): PromptSeccion | null {
    return (
      this.secciones.find(
        (s) => s.idPromptSeccion !== this.tabActiva && this.coincidencias(s) > 0
      ) || null
    );
  }

  /* ================= Copiar ================= */

  /**
   * Copia el prompt al portapapeles.
   *
   * Es la acción principal del módulo: todo lo demás existe para que
   * alguien llegue aquí. Por eso el botón está en cada tarjeta y no
   * escondido dentro del detalle.
   */
  copiar(p: Prompt): void {
    const texto = p.textoPrompt || "";
    const listo = () => {
      this.copiado = p.idPrompt ?? null;
      this.cdr.detectChanges();
      // El aviso se quita solo: un "copiado" permanente deja de
      // significar algo después del segundo uso. Si se copia otro antes
      // de que se apague, el temporizador viejo no debe apagar el nuevo.
      clearTimeout(this.timerCopiado);
      this.timerCopiado = setTimeout(() => {
        this.copiado = null;
        this.cdr.detectChanges();
      }, 1800);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(texto).then(listo, () => this.copiaVieja(texto, listo));
      return;
    }
    // Sin HTTPS, navigator.clipboard no existe. COS corre en red
    // interna, así que el camino viejo tiene que seguir ahí.
    this.copiaVieja(texto, listo);
  }

  private copiaVieja(texto: string, listo: () => void): void {
    const area = document.createElement("textarea");
    area.value = texto;
    area.style.position = "fixed";
    area.style.left = "-9999px";
    document.body.appendChild(area);
    area.select();
    // execCommand no lanza cuando el navegador niega el portapapeles:
    // devuelve false. Sin mirarlo, el botón decía "Copiado" con el
    // portapapeles vacío.
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    } finally {
      document.body.removeChild(area);
    }
    if (ok) {
      listo();
    } else {
      swal.fire("No se pudo copiar", "Selecciona el texto y cópialo a mano.", "warning");
    }
  }

  /**
   * Abre el prompt completo.
   *
   * En modal y no desplegado dentro de la tarjeta: desplegando, la
   * rejilla salta y las tarjetas de al lado se reacomodan. En modal, el
   * catálogo se queda quieto detrás.
   */
  ver(p: Prompt): void {
    this.detalle = p;
  }

  cerrarDetalle(): void {
    this.detalle = null;
  }

  /**
   * «Editar» desde la vista de lectura. Cierra la lectura antes de abrir
   * el formulario: `detalle` es el prompt como estaba al abrirlo, y al
   * guardar se recarga el catálogo pero no esa referencia; si se quedara
   * abierta detrás, enseñaría el texto viejo.
   */
  editarDesdeDetalle(): void {
    const p = this.detalle;
    if (!p) {
      return;
    }
    this.detalle = null;
    this.editar(p);
  }

  /** Cuántas líneas tiene el prompt, para anticiparlo en la tarjeta. */
  lineas(p: Prompt): number {
    return this.lineasTexto(p).length;
  }

  /** «42 líneas», o «1 línea». */
  textoLineas(p: Prompt | null): string {
    const n = p ? this.lineas(p) : 0;
    return n + (n === 1 ? " línea" : " líneas");
  }

  /**
   * El prompt partido en líneas, para pintarlo numerado.
   *
   * Cada línea va en su propio renglón de una rejilla: así el número se
   * queda arriba y alineado aunque el texto se envuelva en varias
   * líneas visuales. Con un `pre` y una columna de números al lado, en
   * cuanto una línea envuelve se desfasa todo lo de abajo.
   */
  lineasTexto(p: Prompt | null): string[] {
    return (p?.textoPrompt || "").split(String.fromCodePoint(10));
  }

  /* ================= Alta y edición ================= */

  /**
   * Cómo estaba el formulario al abrirse, para saber si hay algo sin
   * guardar. Sólo lo usa el Escape: la X y «Cancelar» se pulsan a
   * propósito y descartan sin preguntar, como siempre.
   */
  private fotoEdicion = "";

  get hayCambiosSinGuardar(): boolean {
    const e = this.editando || this.editandoSeccion;
    return !!e && JSON.stringify(e) !== this.fotoEdicion;
  }

  private tomaFoto(): void {
    this.fotoEdicion = JSON.stringify(this.editando || this.editandoSeccion || null);
  }

  private confirmaDescartar(): void {
    swal
      .fire({
        title: "¿Salir sin guardar?",
        text: "Lo que escribiste se pierde.",
        icon: "warning",
        showCancelButton: true,
        // El foco en «Seguir editando»: quien tocó Escape sin querer suele
        // seguir tecleando, y un Enter o un espacio sobre «Salir sin
        // guardar» tiraba justo lo que este aviso protege.
        focusCancel: true,
        confirmButtonText: "Salir sin guardar",
        cancelButtonText: "Seguir editando",
        confirmButtonColor: "#c0392b",
      })
      .then((r) => {
        if (r.isConfirmed) {
          this.cancelar();
          this.cdr.detectChanges();
        }
      });
  }

  nuevoPrompt(idSeccion?: number): void {
    this.editando = {
      idPromptSeccion:
        idSeccion ?? this.tabActiva ?? this.secciones[0]?.idPromptSeccion,
      titulo: "",
      paraQueSirve: "",
      queObtienes: "",
      textoPrompt: "",
      idInventarioIa: null,
      // Sin orden: el servidor lo manda al final de su área. Antes nacía
      // con 0 y se ponía primero, encima de los que más se usan.
      orden: null,
    };
    this.tomaFoto();
  }

  editar(p: Prompt): void {
    // Copia propia: sin ella, escribir en el formulario cambiaría la
    // tarjeta de atrás en vivo y "Cancelar" no revertiría nada.
    this.editando = { ...p };
    this.tomaFoto();
  }

  cancelar(): void {
    this.editando = null;
    this.editandoSeccion = null;
  }

  /**
   * Los topes de cada campo, los MISMOS que exige el servidor
   * (`PromptBuss`: MAX_TITULO, MAX_PARA_QUE, MAX_QUE_OBTIENES, MAX_PROMPT,
   * MAX_NOMBRE_SECCION, MAX_DESCRIPCION, MAX_ICONO) y que aguanta la
   * columna. Aquí sirven para dos cosas: frenar al escribir (`maxlength`)
   * y enseñar cuánto llevas. Si allá cambian, cambian aquí.
   */
  readonly MAX = {
    titulo: 160,
    paraQue: 400,
    queObtienes: 500,
    prompt: 50000,
    nombre: 80,
    descripcion: 300,
    icono: 40,
  };

  largo(texto?: string | null): number {
    return (texto || "").length;
  }

  /** Cerca del tope: a partir del 90% el contador avisa. */
  cerca(texto: string | null | undefined, max: number): boolean {
    return this.largo(texto) >= max * 0.9;
  }

  /**
   * Lo que falta para poder guardar, dicho como se lee: «Falta el título y
   * el prompt». Vacío = se puede guardar.
   *
   * El botón se apaga con esto, y el pie lo dice: un botón apagado sin
   * explicación deja a quien lo mira sin saber qué hacer.
   */
  get faltaPrompt(): string {
    const e = this.editando;
    if (!e) {
      return "";
    }
    const falta: string[] = [];
    if (!(e.titulo || "").trim()) {
      falta.push("el título");
    }
    if (e.idPromptSeccion == null) {
      falta.push("el área");
    }
    if (!(e.textoPrompt || "").trim()) {
      falta.push("el prompt");
    }
    return this.frase(falta);
  }

  get faltaSeccion(): string {
    return this.frase(!(this.editandoSeccion?.nombre || "").trim() ? ["el nombre"] : []);
  }

  /**
   * El área de otro prompt activo que se llama igual, para avisarlo
   * mientras se escribe. Es sólo ayuda: el que decide es el servidor, que
   * además mira los retirados. Se compara como la búsqueda —sin
   * mayúsculas, sin acentos y sin espacios de más— y en cualquier área,
   * porque el catálogo se busca por nombre.
   */
  get tituloRepetido(): PromptSeccion | null {
    const e = this.editando;
    const clave = this.claveTitulo(e?.titulo);
    if (!clave) {
      return null;
    }
    return (
      this.secciones.find((s) =>
        (s.prompts || []).some(
          (p) => p.idPrompt !== e?.idPrompt && this.claveTitulo(p.titulo) === clave
        )
      ) || null
    );
  }

  private claveTitulo(titulo?: string | null): string {
    return this.normaliza(titulo || "").trim().replaceAll(/\s+/g, " ");
  }

  private frase(falta: string[]): string {
    if (!falta.length) {
      return "";
    }
    const lista =
      falta.length === 1
        ? falta[0]
        : falta.slice(0, -1).join(", ") + " y " + falta[falta.length - 1];
    return "Falta " + lista;
  }

  guardar(): void {
    if (!this.editando || this.guardando) {
      return;
    }
    if (!(this.editando.titulo || "").trim()) {
      swal.fire("Falta el título", "Ponle un nombre para poder encontrarlo.", "warning");
      return;
    }
    if (!(this.editando.textoPrompt || "").trim()) {
      swal.fire("Falta el prompt", "Sin el texto no hay nada que copiar.", "warning");
      return;
    }

    this.guardando = true;
    this.service.guardar(this.editando).subscribe({
      next: (res) => {
        this.guardando = false;
        if (res.codigoError) {
          swal.fire("No se guardó", res.mensajeError || "Intenta de nuevo.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.editando = null;
        this.cargar();
      },
      error: () => {
        this.guardando = false;
        swal.fire("No se guardó", "No se pudo guardar el prompt.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  eliminar(p: Prompt): void {
    swal
      .fire({
        title: "¿Eliminar este prompt?",
        text: p.titulo,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#c0392b",
      })
      .then((r) => {
        if (!r.isConfirmed || p.idPrompt == null) {
          return;
        }
        this.service.eliminar(p.idPrompt).subscribe({
          next: (res) => {
            if (res.codigoError) {
              swal.fire("No se eliminó", res.mensajeError || "", "error");
              return;
            }
            this.cargar();
          },
          error: () => swal.fire("No se eliminó", "Intenta de nuevo.", "error"),
        });
      });
  }

  /* ================= Secciones ================= */

  nuevaSeccion(): void {
    this.editandoSeccion = {
      nombre: "",
      descripcion: "",
      // Siempre después de la última pestaña (pedido de Norman, 23/09).
      // Antes era (cuántas hay + 1) × 10, que con huecos o con un orden
      // movido a mano podía caer en medio o empatar con otra.
      orden: this.siguienteOrdenArea(),
      icono: "folder",
    };
    this.iconoInicial = "";
    this.tomaFoto();
  }

  /** El mayor orden de las áreas vivas, más 10. */
  private siguienteOrdenArea(): number {
    return this.secciones.reduce((m, s) => Math.max(m, s.orden ?? 0), 0) + 10;
  }

  /** La última pestaña, para decir «va después de …» en el alta. */
  get ultimaArea(): PromptSeccion | null {
    return this.secciones.length ? this.secciones[this.secciones.length - 1] : null;
  }

  /**
   * Los iconos que se ofrecen para un área. Si el área ya trae uno que no
   * está en la lista (se escribió a mano antes del selector), va primero
   * para que se vea cuál tiene y no se pierda al guardar.
   */
  get iconosArea(): string[] {
    // El de apertura y no el vivo: si fuera el vivo, al escoger otro el
    // propio saldría de la rejilla, todo se recorrería una casilla bajo el
    // puntero y no habría forma de volver a él.
    const propio = this.iconoInicial;
    return propio && !this.ICONOS_AREA.includes(propio)
      ? [propio, ...this.ICONOS_AREA]
      : this.ICONOS_AREA;
  }

  /** El icono que traía el área al abrir el formulario. */
  private iconoInicial = "";

  /** Cómo se llama el icono, en español, para el lector y el title. */
  nombreIcono(i: string): string {
    return this.NOMBRE_ICONO[i] || i;
  }

  /**
   * Flechas dentro del selector de iconos (patrón de radio): la flecha
   * escoge el siguiente o el anterior y le pasa el foco; Inicio y Fin, el
   * primero y el último. Tab entra y sale del grupo de un solo golpe.
   */
  flechasIcono(ev: KeyboardEvent): void {
    if (!this.editandoSeccion) {
      return;
    }
    const lista = this.iconosArea;
    const actual = Math.max(0, lista.indexOf(this.editandoSeccion.icono || "folder"));
    let k: number;
    switch (ev.key) {
      case "ArrowRight":
      case "ArrowDown":
        k = (actual + 1) % lista.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        k = (actual - 1 + lista.length) % lista.length;
        break;
      case "Home":
        k = 0;
        break;
      case "End":
        k = lista.length - 1;
        break;
      default:
        return;
    }
    ev.preventDefault();
    this.editandoSeccion.icono = lista[k];
    this.cdr.detectChanges();
    const botones = (ev.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>(".pr-iconos__b");
    botones[k]?.focus();
  }

  private readonly NOMBRE_ICONO: { [k: string]: string } = {
    rocket_launch: "Cohete",
    category: "Categorías",
    folder: "Carpeta",
    code: "Código",
    terminal: "Terminal",
    coffee: "Taza de café",
    web: "Web",
    phone_iphone: "Teléfono",
    smart_toy: "Robot",
    psychology: "Mente",
    auto_awesome: "Destellos",
    data_object: "Objeto de datos",
    api: "API",
    storage: "Almacenamiento",
    dns: "Servidores",
    cloud: "Nube",
    bug_report: "Bicho (errores)",
    science: "Ciencia",
    security: "Escudo",
    lock: "Candado",
    integration_instructions: "Integración",
    developer_mode: "Modo desarrollador",
    hub: "Conexiones",
    schema: "Esquema",
    analytics: "Analítica",
    query_stats: "Consultas",
    table_chart: "Tabla",
    description: "Documento",
    article: "Artículo",
    school: "Capacitación",
    groups: "Grupo de personas",
    support_agent: "Soporte",
    design_services: "Diseño",
    palette: "Paleta",
    campaign: "Campaña",
    business_center: "Maletín",
    account_balance: "Institución",
    gavel: "Legal",
    fact_check: "Verificación",
    lightbulb: "Idea",
    engineering: "Ingeniería",
    health_and_safety: "Salud y seguridad",
    local_shipping: "Envíos",
    sell: "Ventas",
    translate: "Traducción",
  };

  /** Iconos de Material para las áreas: tecnología primero, luego oficio. */
  readonly ICONOS_AREA = [
    "rocket_launch", "category", "folder", "code", "terminal", "coffee", "web", "phone_iphone",
    "smart_toy", "psychology", "auto_awesome", "data_object", "api", "storage", "dns", "cloud",
    "bug_report", "science", "security", "lock", "integration_instructions", "developer_mode", "hub", "schema",
    "analytics", "query_stats", "table_chart", "description", "article", "school", "groups", "support_agent",
    "design_services", "palette", "campaign", "business_center", "account_balance", "gavel", "fact_check", "lightbulb",
    "engineering", "health_and_safety", "local_shipping", "sell", "translate",
  ];

  editarSeccion(s: PromptSeccion): void {
    this.editandoSeccion = { ...s };
    this.iconoInicial = (s.icono || "").trim();
    this.tomaFoto();
  }

  guardarSeccion(): void {
    if (!this.editandoSeccion || this.guardando) {
      return;
    }
    if (!(this.editandoSeccion.nombre || "").trim()) {
      swal.fire("Falta el nombre", "El área necesita un nombre.", "warning");
      return;
    }

    this.guardando = true;
    // Se manda sin los prompts: el backend no los espera y arrastrarlos
    // sólo engorda la petición.
    const { prompts, ...limpia } = this.editandoSeccion;
    this.service.guardarSeccion(limpia).subscribe({
      next: (res) => {
        this.guardando = false;
        if (res.codigoError) {
          swal.fire("No se guardó", res.mensajeError || "Intenta de nuevo.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.editandoSeccion = null;
        // Si el nombre era el de un área retirada, el servidor la recuperó
        // tal como estaba y la manda completa: se pinta ésa —con su
        // descripción, su icono y su orden de antes—, nunca lo que traía el
        // formulario, y sin esperar a la recarga.
        if (res.seccion) {
          this.colocaSeccion(res.seccion);
        }
        // El área recién guardada se abre sola: es donde va a seguir
        // trabajando quien la creó.
        this.tabActiva = res.idGenerado ?? this.tabActiva;
        this.cargar();
        // Y lo dice: que quien administra sepa que se recuperó, no que se
        // creó otra, y que lo escrito en el formulario no se aplicó.
        if (res.mensaje) {
          swal.fire("Área recuperada", res.mensaje, "info");
        }
      },
      error: () => {
        this.guardando = false;
        swal.fire("No se guardó", "No se pudo guardar el área.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Pone en la lista el área tal como la mandó el servidor, en el lugar
   * que le toca por su orden (y por nombre si empata, como la consulta).
   * Si ya estaba, la reemplaza. La recarga que sigue la deja en su sitio
   * definitivo; esto es para que se vea bien desde el primer instante y
   * también si esa recarga falla.
   */
  private colocaSeccion(s: PromptSeccion): void {
    const lista = this.secciones.filter((x) => x.idPromptSeccion !== s.idPromptSeccion);
    lista.push({ ...s, prompts: s.prompts || [] });
    lista.sort(
      (a, b) =>
        (a.orden ?? 0) - (b.orden ?? 0) || (a.nombre || "").localeCompare(b.nombre || "", "es")
    );
    this.secciones = lista;
  }

  eliminarSeccion(s: PromptSeccion): void {
    const cuantos = (s.prompts || []).length;
    if (cuantos > 0) {
      // Se avisa aquí además de en el backend: es más claro decirlo
      // antes de preguntar que dejar que la confirmación termine en un
      // error.
      swal.fire(
        "El área no está vacía",
        `Tiene ${cuantos} ${cuantos === 1 ? "prompt" : "prompts"} dentro. Muévelos o elimínalos primero.`,
        "warning"
      );
      return;
    }

    swal
      .fire({
        title: "¿Eliminar esta área?",
        text: s.nombre,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#c0392b",
      })
      .then((r) => {
        if (!r.isConfirmed || s.idPromptSeccion == null) {
          return;
        }
        this.service.eliminarSeccion(s.idPromptSeccion).subscribe({
          next: (res) => {
            if (res.codigoError) {
              swal.fire("No se eliminó", res.mensajeError || "", "error");
              return;
            }
            this.cargar();
          },
          error: () => swal.fire("No se eliminó", "Intenta de nuevo.", "error"),
        });
      });
  }

  /* ================= Quién lo ve ================= */

  /**
   * Abre el panel de áreas con lo que ya llegó en consultar(): no hay
   * que esperar una llamada para ver los checks. Se copia para que
   * Cancelar no deje marcas a medias.
   */
  abrirAreas(): void {
    this.areas = this.areasServidor.map((a) => ({ ...a, marcada: !!a.habilitada }));
  }

  cerrarAreas(): void {
    this.areas = null;
  }

  alternaArea(a: AreaMarcable): void {
    if (!a.personas) {
      return;
    }
    a.marcada = !a.marcada;
  }

  /** Cuántas áreas van a quedar marcadas y cuánta gente suman. */
  get resumenAreas(): string {
    const marcadas = (this.areas || []).filter((a) => a.marcada);
    const gente = marcadas.reduce((n, a) => n + (a.personas || 0), 0);
    if (!marcadas.length) {
      return "Ninguna área marcada";
    }
    return `${marcadas.length} ${marcadas.length === 1 ? "área" : "áreas"} · ${gente} ${gente === 1 ? "persona" : "personas"}`;
  }

  /** Las que tienen gente: las únicas que se pueden marcar. */
  get areasConGente(): AreaMarcable[] {
    return (this.areas || []).filter((a) => !!a.personas);
  }

  /** Las que no tienen a nadie en el organigrama: van aparte, abajo. */
  get areasSinGente(): AreaMarcable[] {
    return (this.areas || []).filter((a) => !a.personas);
  }

  /** Cuántas cambiaron respecto a lo guardado, para el pie. */
  get cambiosAreas(): number {
    return (this.areas || []).filter((a) => a.marcada !== !!a.habilitada).length;
  }

  /** Si lo marcado difiere de lo guardado. Sin cambios no hay qué guardar. */
  get areasCambiaron(): boolean {
    return (this.areas || []).some((a) => a.marcada !== !!a.habilitada);
  }

  guardarAreas(): void {
    if (!this.areas || this.guardandoAreas) {
      return;
    }
    const ids = this.areas
      .filter((a) => a.marcada && a.idArea != null)
      .map((a) => a.idArea as number);

    this.guardandoAreas = true;
    this.service.guardarAreas(ids).subscribe({
      next: (res) => {
        this.guardandoAreas = false;
        if (res.codigoError) {
          swal.fire("No se guardó", res.mensajeError || "Intenta de nuevo.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.areasServidor = res.areas || this.areasServidor;
        this.lectores = res.lectores ?? this.lectores;
        this.areas = null;
        this.cdr.detectChanges();
        swal.fire("Listo", res.mensaje || "Áreas guardadas.", "success");
      },
      error: () => {
        this.guardandoAreas = false;
        swal.fire("No se guardó", "No se pudieron guardar las áreas.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Retirados ================= */

  /**
   * Abre la papelera. La lista se pide aquí y no en cada carga: son
   * prompts completos, y el catálogo se consulta muchas más veces de las
   * que alguien viene a recuperar algo.
   */
  abrirRetirados(): void {
    this.retirados = [];
    this.cargandoRetirados = true;
    this.service.retirados().subscribe({
      next: (res) => {
        this.cargandoRetirados = false;
        if (res.codigoError) {
          this.retirados = null;
          swal.fire("No se pudo abrir", res.mensajeError || "Intenta de nuevo.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.retirados = res.retirados || [];
        this.cuantosRetirados = res.cuantosRetirados ?? this.retirados.length;
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoRetirados = false;
        this.retirados = null;
        swal.fire("No se pudo abrir", "No se pudieron cargar los retirados.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  cerrarRetirados(): void {
    this.retirados = null;
  }

  /**
   * Devuelve un prompt al catálogo.
   *
   * Sin confirmación: recuperar no destruye nada y se deshace con el
   * mismo botón de eliminar de siempre. Preguntar aquí sería ruido.
   */
  restaurar(p: Prompt): void {
    if (p.idPrompt == null || this.restaurando != null) {
      return;
    }
    this.restaurando = p.idPrompt;
    this.service.restaurar(p.idPrompt).subscribe({
      next: (res) => {
        this.restaurando = null;
        if (res.codigoError) {
          swal.fire("No se recuperó", res.mensajeError || "Intenta de nuevo.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.retirados = res.retirados || [];
        this.cuantosRetirados = res.cuantosRetirados ?? this.retirados.length;
        // El catálogo de atrás tiene que enseñarlo ya: vuelve a su área.
        this.cargar();
        if (res.mensaje) {
          swal.fire("Recuperado", res.mensaje, "success");
        }
      },
      error: () => {
        this.restaurando = null;
        swal.fire("No se recuperó", "No se pudo recuperar el prompt.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Color por sección ================= */

  /*
   * Tonos apagados y de saturación pareja: puestos uno junto a otro
   * ninguno grita más que los demás, que es lo que pasa cuando se
   * mezcla un rojo puro con un azul apagado.
   */
  private readonly TONOS = [
    "#4361A4", // azul
    "#B04A42", // terracota
    "#2E7F78", // verde azulado
    "#9A6B2F", // ámbar
    "#6E549B", // violeta
    "#A85278", // rosa
    "#3E7A4E", // verde
    "#8A5C3E", // tierra
  ];

  /**
   * El color de una sección.
   *
   * Se saca del id y no de la posición: así renombrarla o moverla de
   * lugar no le cambia el color, y la gente no tiene que reaprender el
   * código de colores cada vez que se reordena el catálogo.
   */
  colorSeccion(s: PromptSeccion): string {
    // Las áreas por tecnología llevan tono fijo, por nombre: que Java
    // sea siempre el mismo naranja y Angular el mismo rojo en QA y en
    // producción, aunque sus ids no coincidan. Cualquier otra área que
    // se dé de alta después cae en la paleta por id, como antes.
    const nombre = (s.nombre || "").trim().toLowerCase();
    if (this.TONO_AREA[nombre]) {
      return this.TONO_AREA[nombre];
    }
    const id = s.idPromptSeccion ?? 0;
    return this.TONOS[id % this.TONOS.length];
  }

  /** Tono fijo de las áreas conocidas. Mismo criterio que la paleta: apagados, legibles en blanco. */
  private readonly TONO_AREA: { [k: string]: string } = {
    arranque: "#A0446E", // rosa oscuro: es la única pestaña que no es una tecnología
    general: "#6E549B", // violeta
    java: "#A5621F", // naranja café
    angular: "#B04A42", // rojo terracota
    flutter: "#2F6FA8", // azul
    react: "#2E7F78", // verde azulado
  };

  /** El mismo color, en la transparencia que se le pida. */
  tinte(hex: string, alfa: number): string {
    const h = (hex || "#4361A4").replace("#", "");
    const r = Number.parseInt(h.substring(0, 2), 16);
    const g = Number.parseInt(h.substring(2, 4), 16);
    const b = Number.parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alfa})`;
  }

  /* ---------- Color del prompt ----------
   *
   * El del área a la que pertenece. Hasta el 10/09/2026 era el del
   * asistente (verde Codex, rojo Claude); se cambió porque los prompts
   * sirven con cualquiera de los dos y ese color decía algo que ya no
   * importaba al escoger. El área sí: es lo que la pestaña, la tarjeta
   * y el botón de copiar tienen en común, y verlos del mismo color dice
   * de un vistazo en qué parte del catálogo estás.
   */
  colorDe(p: Prompt | null): string {
    const s = this.seccionDe(p);
    return s ? this.colorSeccion(s) : "#6A7A88";
  }

  /** El área de un prompt, para su nombre e icono en la lectura. */
  seccionDe(p: Prompt | null): PromptSeccion | null {
    return p ? this.secciones.find((x) => x.idPromptSeccion === p.idPromptSeccion) || null : null;
  }

  /* ================= Utilidades ================= */

  trackSeccion = (_: number, s: PromptSeccion) => s.idPromptSeccion;
  trackLinea = (i: number) => i;
  trackPrompt = (_: number, p: Prompt) => p.idPrompt;
  trackArea = (_: number, a: PromptArea) => a.idArea;

  nombreSistema(id?: number | null): string {
    if (id == null) {
      return "";
    }
    const s = this.sistemas.find((x) => x.idInventarioIa === id);
    return s ? s.nombreIa || "" : "";
  }
}
